import { Card, CardProps } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { DateField } from 'components/Field/DateField';
import { ImageField } from 'componentsAware/ImageField';
import { OptionField } from 'components/Field/OptionField';
import { PinField } from 'components/Field/PinField';
import { QRField } from 'components/Field/QRField';
import { QRFieldDisplay } from 'components/Field/QRFieldDisplay';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { TextField } from 'components/Field/TextField';
import { TimeField } from 'components/Field/TimeField';
import { ICONS } from 'components/Icon';
import { ModalScreens } from 'components/Navigation/types';
import { NumberRow } from 'components/Row/NumberRow';
import { format } from 'date-fns';
import { UI_DATE_FORMAT_SHORT } from 'lib/constants';
import { pluralise } from 'lib/pluralise';
import { appendPunctuation, arrayUnique, randomKey } from 'lib/riskbase';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { ChildGroupAssessment } from 'screens/FieldComponents/ChildGroupAssessment';
import { ChildGroupAsset } from 'screens/FieldComponents/ChildGroupAsset';
import { ChildGroupLightAssessment } from 'screens/FieldComponents/ChildGroupLightAssessment';
import { ChildGroupLightAsset } from 'screens/FieldComponents/ChildGroupLightAsset';
import { PinDisplayRowAssessment } from 'screens/FieldComponents/PinDisplayRowAssessment';
import { PinDisplayRowAsset } from 'screens/FieldComponents/PinDisplayRowAsset';
import {
  eventSelectScreenAdded,
  eventSelectScreenSelection,
  NAVIGATE_MODAL,
} from 'store/actions';
import { FieldTypes } from 'store/assets/types';
import { FieldField } from 'store/organisations/types';
import { PinNew, PinNewWithName } from 'store/plan/types';
import {
  AppContext,
  AppContextEnum,
  FilesContextEnum,
  SelectOption,
  appContextToFileContext,
} from 'types/types';
import { FileField } from 'componentsAware/FileField';
import { FileDisplay } from 'componentsAware/FileDisplay';
import { ImageDisplay } from 'componentsAware/ImageDisplay';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { FieldShouldBeHidden } from './FieldShouldBeHidden';

interface Props {
  context: AppContext;
  field: FieldField;
  value: string | string[] | undefined;
  onChange: (
    name: string,
    value: string | string[] | null,
    pin?: PinNew | '' | null,
  ) => void;
  onRevert?: (fieldId: string) => void;
  pin?: PinNew | '';
  pins?: PinNewWithName[];
  shouldBeHidden?: boolean;
  textSuggestions?: string[];
  explanation?: CardProps['explanation'];
  showRevert?: boolean;
  previousPin?: PinNew;
  previousValue?: string | string[];
  editable: boolean;
  required: boolean;
  onEsc?: () => void;
  instanceId: string | null;
  // True to show invalid state of field (if required and has no value)
  showInvalid: boolean;
}

export const Field: React.FC<Props> = ({
  context,
  field,
  onChange,
  onRevert,
  pin,
  pins,
  value,
  shouldBeHidden,
  textSuggestions,
  explanation,
  showRevert,
  previousPin,
  previousValue,
  editable = false,
  required = false,
  onEsc,
  instanceId,
  showInvalid,
}) => {
  const dispatch = useDispatch();

  const renderKey = React.useRef(randomKey());
  const incomingValueKey = React.useRef(
    `RepeatableFieldGroup-${context.assetId}-${field.id}--${Math.random()}`,
  );
  const eventKeySelectScreenSelection = React.useRef(randomKey());

  const handleChange = React.useCallback(
    (newValue: string | string[]) => onChange(field.id, newValue),
    [field.id, onChange],
  );

  const handleChangePin = React.useCallback(
    (newPin: PinNew | '' | null) => onChange(field.id, '', newPin),
    [field.id, onChange],
  );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenAdded, (event) => {
        if (event.payload.key === eventKeySelectScreenSelection.current) {
          const newValue = event.payload.stringValue;
          if (newValue) {
            if (field.type === FieldTypes.NOTE) {
              renderKey.current = randomKey();
              handleChange(value ? `${value}\n\n${newValue}` : newValue);
            } else if (field.type === FieldTypes.TEXT) {
              renderKey.current = randomKey();
              handleChange(
                value
                  ? `${appendPunctuation(value as string, '.')} ${newValue}`
                  : newValue,
              );
            }
          }
        }
      }),
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === eventKeySelectScreenSelection.current) {
          const newValue = event.payload.stringValues[0];
          if (newValue) {
            renderKey.current = randomKey();
            handleChange(newValue);
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [field.type, handleChange, value]);

  const hasOnLabelLinkPress =
    textSuggestions?.length || field.suggestions.length;

  const handleLabelLinkPress = React.useCallback(() => {
    if (textSuggestions?.length || field.suggestions.length) {
      const options: SelectOption[] = [];
      if (field.suggestions.length) {
        field.suggestions.forEach((suggestion) => {
          options.push({
            name: suggestion,
            internalValue: suggestion,
          });
        });
      }
      if (textSuggestions?.length) {
        arrayUnique(textSuggestions.sort()).forEach((suggestion) => {
          if (!field.suggestions.includes(suggestion)) {
            options.push({
              name: suggestion,
              internalValue: suggestion,
              groupName: field.suggestions.length
                ? 'Other Suggestions'
                : undefined,
            });
          }
        });
      }

      const typedValue = value as string;

      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.SELECT,
          params: {
            title: field.label,
            options,
            text:
              "Press a suggestion to replace the field's value. " +
              "Press a plus button to append the suggestion to the field's existing value",
            value: typedValue ? [typedValue] : [],
            hideRadioButton: true,
            eventKey: eventKeySelectScreenSelection.current,
            eventKeyAdded: eventKeySelectScreenSelection.current,
          },
        }),
      );
    }
  }, [dispatch, field.label, field.suggestions, textSuggestions, value]);

  const explanationLinkIcon = ICONS.ClockRotateLeftLight;
  const onExplanationLinkPress = React.useCallback(
    () => onRevert?.(field.id),
    [field.id, onRevert],
  );

  switch (field.type) {
    /**
     * TEXT
     */
    case FieldTypes.TEXT: {
      if (!editable) {
        return (
          <Card
            label={field.label}
            explanation={explanation}
            required={required}
            invalid={showInvalid && required && !value}
          >
            <CardRow value={value as string} placeholder="No Value" />
          </Card>
        );
      }

      const explanationLinkText = showRevert
        ? (previousValue as string) || 'No Value'
        : undefined;

      return (
        <Card
          label={field.label}
          required={required}
          invalid={showInvalid && required && !value}
          onLabelLinkPress={
            hasOnLabelLinkPress ? handleLabelLinkPress : undefined
          }
          labelLinkText="Suggestions"
          explanation={explanation}
          explanationLinkText={explanationLinkText}
          explanationLinkIcon={explanationLinkIcon}
          onExplanationLinkPress={onExplanationLinkPress}
        >
          <TextField
            label={field.label}
            required={required}
            key={renderKey.current}
            onChange={handleChange}
            value={(value as string) || ''}
            placeholder="No Value"
            showTextIcon={false}
            onEsc={onEsc}
          />
          {shouldBeHidden && (
            <FieldShouldBeHidden
              fieldLabel={field.label}
              onClearAndHide={() => handleChange('')}
            />
          )}
        </Card>
      );
    }

    /**
     * NOTE
     */
    case FieldTypes.NOTE: {
      if (!editable) {
        return (
          <Card
            label={field.label}
            explanation={explanation}
            required={required}
            invalid={showInvalid && required && !value}
          >
            <CardRow value={value as string} placeholder="No Value" />
          </Card>
        );
      }

      const explanationLinkText = showRevert
        ? (previousValue as string) || 'No Value'
        : undefined;
      return (
        <Card
          label={field.label}
          required={required}
          invalid={showInvalid && required && !value}
          onLabelLinkPress={
            hasOnLabelLinkPress ? handleLabelLinkPress : undefined
          }
          labelLinkText="Suggestions"
          explanation={explanation}
          explanationLinkText={explanationLinkText}
          explanationLinkIcon={explanationLinkIcon}
          onExplanationLinkPress={onExplanationLinkPress}
        >
          <TextAreaField
            label={field.label}
            required={required}
            key={renderKey.current}
            onChange={handleChange}
            value={value as string}
            placeholder="No Value"
            onEsc={onEsc}
            showFieldTypeIcon={false}
          />
          {shouldBeHidden && (
            <FieldShouldBeHidden
              fieldLabel={field.label}
              onClearAndHide={() => handleChange('')}
            />
          )}
        </Card>
      );
    }

    /**
     * NUMBER
     */
    case FieldTypes.NUMBER: {
      if (!editable) {
        return (
          <Card
            label={field.label}
            explanation={explanation}
            required={required}
            invalid={showInvalid && required && !value}
          >
            <CardRow value={value as string} placeholder="No Value" />
          </Card>
        );
      }

      const explanationLinkText = showRevert
        ? (previousValue as string) || 'No Value'
        : undefined;

      return (
        <Card
          label={field.label}
          required={required}
          invalid={showInvalid && required && !value}
          explanation={explanation}
          explanationLinkText={explanationLinkText}
          explanationLinkIcon={explanationLinkIcon}
          onExplanationLinkPress={onExplanationLinkPress}
        >
          <NumberRow
            value={value ? parseInt(value as string, 10) : undefined}
            onChangeAsString={handleChange}
            showFieldTypeIcon={false}
          />
          {shouldBeHidden && (
            <FieldShouldBeHidden
              fieldLabel={field.label}
              onClearAndHide={() => handleChange('')}
            />
          )}
        </Card>
      );
    }

    /**
     * TIME
     */
    case FieldTypes.TIME: {
      if (!editable) {
        return (
          <Card
            label={field.label}
            explanation={explanation}
            required={required}
            invalid={showInvalid && required && !value}
          >
            <CardRow value={value as string} placeholder="No Value" />
          </Card>
        );
      }

      const explanationLinkText = showRevert
        ? (previousValue as string) || 'No Value'
        : undefined;
      return (
        <Card
          label={field.label}
          required={required}
          invalid={showInvalid && required && !value}
          explanation={explanation}
          explanationLinkText={explanationLinkText}
          explanationLinkIcon={explanationLinkIcon}
          onExplanationLinkPress={onExplanationLinkPress}
        >
          <TimeField
            value={(value as string) || ''}
            onChange={handleChange}
            placeholder="No Time"
            enforceInputWidth
          />
          {shouldBeHidden && (
            <FieldShouldBeHidden
              fieldLabel={field.label}
              onClearAndHide={() => handleChange('')}
            />
          )}
        </Card>
      );
    }
    case FieldTypes.DATE: {
      if (!editable) {
        return (
          <Card
            label={field.label}
            explanation={explanation}
            required={required}
            invalid={showInvalid && required && !value}
          >
            <CardRow
              value={
                value
                  ? format(new Date(value as string), UI_DATE_FORMAT_SHORT)
                  : ''
              }
              placeholder="No Value"
            />
          </Card>
        );
      }

      const explanationLinkText = showRevert
        ? previousValue
          ? format(new Date(previousValue as string), UI_DATE_FORMAT_SHORT)
          : 'No Value'
        : undefined;
      return (
        <Card
          label={field.label}
          required={required}
          invalid={showInvalid && required && !value}
          explanation={explanation}
          explanationLinkText={explanationLinkText}
          explanationLinkIcon={explanationLinkIcon}
          onExplanationLinkPress={onRevert ? onExplanationLinkPress : undefined}
        >
          <DateField
            placeholder="No Date"
            value={(value as string) || ''}
            onChange={handleChange}
          />
          {shouldBeHidden && (
            <FieldShouldBeHidden
              fieldLabel={field.label}
              onClearAndHide={() => handleChange('')}
            />
          )}
        </Card>
      );
    }

    /**
     * OPTION
     */
    case FieldTypes.OPTION: {
      const valueTyped = value as string[] | undefined;
      if (!editable) {
        return (
          <Card
            label={field.label}
            explanation={explanation}
            required={required}
            invalid={showInvalid && required && !valueTyped?.length}
          >
            <CardRow
              value={
                valueTyped?.length
                  ? field.options
                      .filter((option) => valueTyped.includes(option.id))
                      .map((option) => option.name)
                      .join(', ')
                  : ''
              }
              placeholder="No Value"
            />
          </Card>
        );
      }

      const previousValueTyped = previousValue as string[] | undefined;
      const explanationLinkText = showRevert
        ? previousValueTyped?.length
          ? field.options
              .filter((option) => previousValueTyped.includes(option.id))
              .map((option) => option.name)
              .join(', ') || 'No Value'
          : 'No Value'
        : undefined;
      return (
        <Card
          label={field.label}
          required={required}
          invalid={showInvalid && required && !valueTyped?.length}
          explanation={explanation}
          explanationLinkText={explanationLinkText}
          explanationLinkIcon={explanationLinkIcon}
          onExplanationLinkPress={onRevert ? onExplanationLinkPress : undefined}
        >
          <OptionField
            modalLabel={field.label}
            key={incomingValueKey.current}
            value={valueTyped || []}
            multiSelect={field.multiSelect}
            onChange={handleChange}
            options={field.options.map((option) => ({
              internalValue: option.id,
              name: option.name,
            }))}
            expandedInlineFirst
            expandedInlineLast
            showNoValueOption
          />
          {shouldBeHidden && (
            <FieldShouldBeHidden
              fieldLabel={field.label}
              onClearAndHide={() => handleChange([])}
            />
          )}
        </Card>
      );
    }

    /**
     * IMAGE
     */
    case FieldTypes.IMAGE: {
      const valueTyped = value as string[] | undefined;
      if (!editable) {
        return (
          <Card
            label={field.label}
            explanation={explanation}
            required={required}
            invalid={showInvalid && required && !valueTyped?.length}
          >
            {valueTyped ? (
              <ImageDisplay
                value={valueTyped}
                context={appContextToFileContext(context)}
              />
            ) : (
              <CardRow
                value=""
                placeholder={`No ${field.multiSelect ? 'Images' : 'Image'}`}
              />
            )}
          </Card>
        );
      }

      const previousValueTyped = previousValue as string[] | undefined;
      const explanationLinkText = showRevert
        ? previousValueTyped?.length
          ? pluralise(previousValueTyped.length, '1 Image', '{x} Images')
          : `No ${field.multiSelect ? 'Images' : 'Image'}`
        : undefined;
      return (
        <Card
          label={field.label}
          required={required}
          invalid={showInvalid && required && !valueTyped?.length}
          explanation={explanation}
          explanationLinkText={explanationLinkText}
          explanationLinkIcon={explanationLinkIcon}
          onExplanationLinkPress={onRevert ? onExplanationLinkPress : undefined}
        >
          <ImageField
            context={appContextToFileContext(context)}
            value={valueTyped || []}
            onChange={handleChange}
            multiSelect={field.multiSelect}
            promptMessage={field.multiSelect ? 'Add Images' : 'Add Image'}
          />
          {shouldBeHidden && (
            <FieldShouldBeHidden
              fieldLabel={field.label}
              onClearAndHide={() => handleChange([])}
            />
          )}
        </Card>
      );
    }

    /**
     * FILE
     */
    case FieldTypes.FILE: {
      const valueTyped = value as string[] | undefined;
      if (!editable) {
        return (
          <Card
            label={field.label}
            explanation={explanation}
            required={required}
            invalid={showInvalid && required && !valueTyped?.length}
          >
            <FileDisplay
              context={appContextToFileContext(context)}
              value={valueTyped || []}
              placeholder={`No ${field.multiSelect ? 'Files' : 'File'}`}
            />
          </Card>
        );
      }

      const previousValueTyped = previousValue as string[] | undefined;
      const explanationLinkText = showRevert
        ? previousValueTyped?.length
          ? pluralise(previousValueTyped.length, '1 File', '{x} Files')
          : `No ${field.multiSelect ? 'Files' : 'File'}`
        : undefined;
      return (
        <Card
          label={field.label}
          required={required}
          invalid={showInvalid && required && !valueTyped?.length}
          explanation={explanation}
          explanationLinkText={explanationLinkText}
          explanationLinkIcon={explanationLinkIcon}
          onExplanationLinkPress={onRevert ? onExplanationLinkPress : undefined}
        >
          <FileField
            context={
              context.context === AppContextEnum.localAsset
                ? {
                    context: FilesContextEnum.localAsset,
                    assetId: context.assetId,
                  }
                : {
                    context:
                      context.context === AppContextEnum.remoteAssessment
                        ? FilesContextEnum.remoteAssessment
                        : FilesContextEnum.localAssessment,
                    assetId: context.assetId,
                    assessmentId: context.assessmentId,
                  }
            }
            value={valueTyped || []}
            onChange={handleChange}
            multiSelect={field.multiSelect}
            promptMessage={field.multiSelect ? 'Add Files' : 'Add File'}
          />
          {shouldBeHidden && (
            <FieldShouldBeHidden
              fieldLabel={field.label}
              onClearAndHide={() => handleChange([])}
            />
          )}
        </Card>
      );
    }

    /**
     * PIN
     */
    case FieldTypes.PIN: {
      if (!editable) {
        return (
          <Card
            label={field.label}
            explanation={explanation}
            required={required}
            invalid={showInvalid && required && !pin}
          >
            {context.context === AppContextEnum.localAsset ? (
              <PinDisplayRowAsset
                assetId={context.assetId}
                pin={pin || undefined}
              />
            ) : (
              <PinDisplayRowAssessment pin={pin || undefined} />
            )}
          </Card>
        );
      }
      const explanationLinkText = showRevert
        ? previousPin
          ? 'Pin'
          : 'No Pin'
        : undefined;
      return (
        <Card
          label={field.label}
          required={required}
          invalid={showInvalid && required && !pin}
          explanation={explanation}
          explanationLinkText={explanationLinkText}
          explanationLinkIcon={explanationLinkIcon}
          onExplanationLinkPress={onRevert ? onExplanationLinkPress : undefined}
        >
          <PinField
            pin={pin || ''}
            pins={pins || []}
            assetId={context.assetId}
            onChange={handleChangePin}
          />
          {shouldBeHidden && (
            <FieldShouldBeHidden
              fieldLabel={field.label}
              onClearAndHide={() => handleChangePin('')}
            />
          )}
        </Card>
      );
    }

    /**
     * QR
     */
    case FieldTypes.QR: {
      if (!editable) {
        return (
          <Card
            label={field.label}
            explanation={explanation}
            required={required}
            invalid={showInvalid && required && !value}
          >
            <QRFieldDisplay value={(value || '') as string} showIconOnLeft />
          </Card>
        );
      }

      const explanationLinkText = showRevert
        ? previousValue
          ? 'QR Code'
          : 'No QR Code'
        : undefined;
      return (
        <Card
          label={field.label}
          required={required}
          invalid={showInvalid && required && !value}
          explanation={explanation}
          explanationLinkText={explanationLinkText}
          explanationLinkIcon={explanationLinkIcon}
          onExplanationLinkPress={onRevert ? onExplanationLinkPress : undefined}
        >
          <QRField value={(value as string) || ''} onChange={handleChange} />
          {shouldBeHidden && (
            <FieldShouldBeHidden
              fieldLabel={field.label}
              onClearAndHide={() => handleChange('')}
            />
          )}
        </Card>
      );
    }

    /**
     * QR
     */
    case FieldTypes.GROUP: {
      return context.context === AppContextEnum.localAsset ? (
        <ChildGroupAsset
          fieldId={field.id}
          assetId={context.assetId}
          parentInstanceId={instanceId}
          childGroupId={field.childGroupId!}
        />
      ) : (
        <ChildGroupAssessment
          parentFieldId={field.id}
          parentInstanceId={instanceId}
          childGroupId={field.childGroupId!}
          isModal={context.isModal}
        />
      );
    }

    /**
     * GROUPLIGHT
     */
    case FieldTypes.GROUPLIGHT: {
      return context.context === AppContextEnum.localAsset ? (
        <ChildGroupLightAsset
          assetId={context.assetId}
          parentFieldId={field.id}
          parentInstanceId={instanceId}
          childGroupId={field.childGroupId!}
        />
      ) : (
        <ChildGroupLightAssessment
          parentFieldId={field.id}
          parentInstanceId={instanceId}
          childGroupId={field.childGroupId!}
          value={(value || []) as string[]}
        />
      );
    }

    default:
      return (
        <Card label="Unknown" explanation={explanation}>
          <CardRow value="Unknown Field Type" />
        </Card>
      );
  }
};
